/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CostCenterResponse } from '../models/cost-center-response';
import { CreateCostCenterRequest } from '../models/create-cost-center-request';
import { GenericResponse } from '../models/generic-response';
import { GetCostCenterResponse } from '../models/get-cost-center-response';
import { UpdateCostCenterRequest } from '../models/update-cost-center-request';

/**
 * Cost Center APIs
 */
@Injectable({ providedIn: 'root' })
export class CostCenterService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllCostCenters()` */
  static readonly GetAllCostCentersPath = '/api/v1/aircrafts/cost-centers';

  /**
   * Get cost center list.
   *
   * Returns all cost centers as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllCostCenters()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllCostCenters$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - costCenter
       * - label
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetCostCenterResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CostCenterService.GetAllCostCentersPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetCostCenterResponse>;
        })
      );
  }

  /**
   * Get cost center list.
   *
   * Returns all cost centers as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllCostCenters$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllCostCenters(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - costCenter
       * - label
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetCostCenterResponse> {
    return this.getAllCostCenters$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetCostCenterResponse>): GetCostCenterResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createCostCenter()` */
  static readonly CreateCostCenterPath = '/api/v1/aircrafts/cost-centers';

  /**
   * Create new cost center.
   *
   * Registers a new Cost Center to the backend.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createCostCenter()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCostCenter$Response(
    params: {
      body: CreateCostCenterRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CostCenterResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CostCenterService.CreateCostCenterPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CostCenterResponse>;
        })
      );
  }

  /**
   * Create new cost center.
   *
   * Registers a new Cost Center to the backend.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createCostCenter$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createCostCenter(
    params: {
      body: CreateCostCenterRequest;
    },
    context?: HttpContext
  ): Observable<CostCenterResponse> {
    return this.createCostCenter$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CostCenterResponse>): CostCenterResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getCostCenterByCostCenter()` */
  static readonly GetCostCenterByCostCenterPath =
    '/api/v1/aircrafts/cost-centers/{id}';

  /**
   * Get cost center by cost center.
   *
   * Fetches details of an cost center.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getCostCenterByCostCenter()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostCenterByCostCenter$Response(
    params: {
      /**
       * The cost center name
       */
      id: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CostCenterResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CostCenterService.GetCostCenterByCostCenterPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CostCenterResponse>;
        })
      );
  }

  /**
   * Get cost center by cost center.
   *
   * Fetches details of an cost center.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getCostCenterByCostCenter$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getCostCenterByCostCenter(
    params: {
      /**
       * The cost center name
       */
      id: string;
    },
    context?: HttpContext
  ): Observable<CostCenterResponse> {
    return this.getCostCenterByCostCenter$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CostCenterResponse>): CostCenterResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `deleteCostCenter()` */
  static readonly DeleteCostCenterPath = '/api/v1/aircrafts/cost-centers/{id}';

  /**
   * Delete Cost Center by id.
   *
   * Marks an existing Cost Center as deleted.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteCostCenter()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCostCenter$Response(
    params: {
      /**
       * The cost center name
       */
      id: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CostCenterService.DeleteCostCenterPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Cost Center by id.
   *
   * Marks an existing Cost Center as deleted.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteCostCenter$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteCostCenter(
    params: {
      /**
       * The cost center name
       */
      id: string;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteCostCenter$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateCostCenter()` */
  static readonly UpdateCostCenterPath = '/api/v1/aircrafts/cost-centers/{id}';

  /**
   * Update Cost Center by id.
   *
   * Updates an existing Cost Center details, or else returns a 404 error.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateCostCenter()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCostCenter$Response(
    params: {
      /**
       * The cost center name
       */
      id: string;
      body: UpdateCostCenterRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CostCenterResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CostCenterService.UpdateCostCenterPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CostCenterResponse>;
        })
      );
  }

  /**
   * Update Cost Center by id.
   *
   * Updates an existing Cost Center details, or else returns a 404 error.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateCostCenter$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateCostCenter(
    params: {
      /**
       * The cost center name
       */
      id: string;
      body: UpdateCostCenterRequest;
    },
    context?: HttpContext
  ): Observable<CostCenterResponse> {
    return this.updateCostCenter$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CostCenterResponse>): CostCenterResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getAllCostCentersByClientId()` */
  static readonly GetAllCostCentersByClientIdPath =
    '/api/v1/{clientId}/aircraft/cost-centers';

  /**
   * Get cost center list by client.
   *
   * Returns all cost centers as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllCostCentersByClientId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllCostCentersByClientId$Response(
    params: {
      /**
       * This adds a string search to the following fields:
       * - costCenter
       * - label
       */
      search?: string;

      /**
       * The client id
       */
      clientId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<CostCenterResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      CostCenterService.GetAllCostCentersByClientIdPath,
      'get'
    );
    if (params) {
      rb.query('search', params.search, {});
      rb.path('clientId', params.clientId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<CostCenterResponse>;
        })
      );
  }

  /**
   * Get cost center list by client.
   *
   * Returns all cost centers as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllCostCentersByClientId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllCostCentersByClientId(
    params: {
      /**
       * This adds a string search to the following fields:
       * - costCenter
       * - label
       */
      search?: string;

      /**
       * The client id
       */
      clientId: number;
    },
    context?: HttpContext
  ): Observable<CostCenterResponse> {
    return this.getAllCostCentersByClientId$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<CostCenterResponse>): CostCenterResponse =>
          r.body
      )
    );
  }
}
